import React, { useContext, useState } from 'react'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import TagIcon from 'tag/icon'
import UserContext from 'context/user-context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faFilter,
  faTimesCircle,
  faBookOpen
} from '@fortawesome/free-solid-svg-icons'
import WeightBadge from 'shared/badges/weight-badge'
import { useAnalyticPolicyRules } from 'api/hooks/use-analytic-policy-rules'
import MutedText from 'shared/forms/muted-text'

const confidenceBg = (c) => c >= 0.8 ? 'success' : c >= 0.6 ? 'warning' : 'secondary'

const PolicyRulesDetails = ({ data }) => (
  <div className="ms-3 mt-2 mb-1">
    {data.map((pra) => (
      <div
        key={pra.id}
        className="d-flex align-items-start mb-1 ps-2"
        style={{ borderLeft: '3px solid #6c757d' }}
      >
        <div className="flex-grow-1">
          <small className="fw-bold">{pra.policy_rule.summary || pra.policy_rule.category}</small>
          <Badge
            bg={confidenceBg(pra.confidence)}
            className="ms-2"
            style={{ fontSize: '0.65rem' }}
          >
            {Math.round(pra.confidence * 100)}%
          </Badge>
          <small className="text-muted d-block">{pra.policy_rule.document_name}</small>
          {pra.reasoning && (
            <small className="text-muted fst-italic d-block">{pra.reasoning}</small>
          )}
        </div>
      </div>
    ))}
  </div>
)

const PolicyRulesBadge = ({ analyticId, expanded, onToggle }) => {
  const { data, isLoading } = useAnalyticPolicyRules({ analyticId })

  if (isLoading) {
    return <Spinner animation="border" size="sm" style={{ width: '0.6rem', height: '0.6rem' }} className="ms-2" />
  }

  if (!data || data.length === 0) return null

  return (
    <>
      <Button
        variant="link"
        size="sm"
        className="p-0 ms-2 text-decoration-none"
        onClick={(e) => { e.stopPropagation(); onToggle() }}
      >
        <FontAwesomeIcon icon={faBookOpen} className="me-1" />
        <small>{data.length} policy rule{data.length !== 1 ? 's' : ''}</small>
        <small className="ms-1">{expanded ? '\u25B2' : '\u25BC'}</small>
      </Button>
      {expanded && <PolicyRulesDetails data={data} />}
    </>
  )
}

const AnalyticRow = ({ a, reportAnalytics, isMuted }) => {
  const { municipality } = useContext(UserContext)
  const [expanded, setExpanded] = useState(false)

  return (
    <tr
      key={`transaction-analytic-row-${a.id}`}
      className={isMuted ? 'text-muted bg-light' : ''}
    >
      <td className="fw-bold" colSpan={2}>
        {a.analytic.name}{' '}
        {a.analytic.is_control && (
          <TagIcon
            tagName="Control"
            tagDescription="Related to a control"
          />
        )}
        {a.analytic?.weight && <WeightBadge weight={a.analytic.weight} />}
        {!isMuted && reportAnalytics.includes(a.analytic.id) && (
          <Badge
            className="text-primary bg-white float-end"
            style={{ cursor: 'help' }}
          >
            <FontAwesomeIcon icon={faFilter} className="me-1" /> Filter
          </Badge>
        )}
        {isMuted && municipality.analytics_turned_off.includes(a.analytic.id) && (
          <Badge className="bg-white text-info">
            <FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Off
          </Badge>
        )}
        {isMuted && a.is_resolved && (
          <OverlayTrigger
            placement="top"
            trigger={['hover', 'focus']}
            overlay={
              <Tooltip id={'tooltip'}>
                This analytic has been resolved and is no longer active.
              </Tooltip>
            }
          >
            <Badge
              className="bg-white text-success"
              style={{ cursor: 'help' }}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />{' '}
              Resolved
            </Badge>
          </OverlayTrigger>
        )}
        <div className="small fw-light ms-2">
          {a.analytic.description}
        </div>
        <MutedText as="div" className="mt-2">
          {a.details?.split('||').map((d, i) => (
            <p key={`analytic-detail-${d}-${i}`} className="mb-0">
              {d}
            </p>
          ))}
        </MutedText>
        {a.flagged_rationale && (
          <MutedText as="div" className="mt-1">
            <span className="small fw-semibold">Matched:</span>
            {a.flagged_rationale.split('||').map((r, i) => (
              <p key={`analytic-rationale-${r}-${i}`} className="mb-0 small">
                {r}
              </p>
            ))}
          </MutedText>
        )}
        <PolicyRulesBadge
          analyticId={a.analytic.id}
          expanded={expanded}
          onToggle={() => setExpanded(!expanded)}
        />
      </td>
    </tr>
  )
}

function AnalyticsTable({ analytics, reportAnalytics = [] }) {
  const { municipality } = useContext(UserContext)

  const analyticsOn = analytics.filter(
    (a) =>
      !municipality.analytics_turned_off.includes(a.analytic.id) &&
      !a.is_resolved
  )
  const analyticsOff = analytics.filter(
    (a) =>
      municipality.analytics_turned_off.includes(a.analytic.id) || a.is_resolved
  )

  return (
    <Table responsive className="border">
      <thead>
        <tr>
          <th style={{ width: '25%' }} className="text-info">
            Name
          </th>
          <th style={{ width: '30%' }} className="text-info">
            Description
          </th>
        </tr>
      </thead>
      <tbody>
        {analyticsOn.map((a) => (
          <AnalyticRow
            key={`transaction-analytic-row-${a.id}`}
            a={a}
            reportAnalytics={reportAnalytics}
            isMuted={false}
          />
        ))}
        {analyticsOff.map((a) => (
          <AnalyticRow
            key={`transaction-analytic-row-${a.id}`}
            a={a}
            reportAnalytics={reportAnalytics}
            isMuted={true}
          />
        ))}
      </tbody>
    </Table>
  )
}

export default AnalyticsTable
